/**
 * Inbox conversation detail — shows message history and allows
 * the business owner to send manual messages (human override).
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { cn } from '@/utils'
import {
  Send,
  Bot,
  User,
  Briefcase,
  Phone,
  AlertTriangle,
  Play,
  CheckCircle,
  RotateCcw,
  History,
  Plus,
  XCircle,
  Paperclip,
  Image as ImageIcon,
  FlaskConical,
  Archive,
  ArchiveRestore,
  StickyNote,
  ChevronDown,
} from 'lucide-react'
import { Button, Badge, Spinner } from '@/components/ui'
import { Tip } from '@/components/ui/Tip'
import type { InboxConversationDetail as DetailType, InboxMessage, InboxMissingFile } from '@/types/inbox'
import { MediaAttachment } from './MediaAttachment'
import { attachmentMeta, viewerItems } from './mediaGallery'
import { EmailMessage } from './EmailMessage'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { emailChips, emailRowMessages } from './emailRows'
import { MissingFileLines } from './MessageFiles'
import { InboxMessageContent } from './InboxMessageContent'
import { cutLine, readCut } from '@/utils/replyCutText'
import { InboxEmailHeader } from './InboxEmailHeader'
import { isNearBottom } from './messageScroll'
import { holdLanding, type LandingHold } from './focusLanding'
import { apiClient } from '@/services/apiClient'
import { useTeamTypingPing } from './useTeamTypingPing'
import { AssetLibraryPicker } from './AssetLibraryPicker'
import { useCanAccessFeature } from '@/hooks/useCanAccessFeature'
import { SaveScenarioModal } from './SaveScenarioModal'
import { EmailDraftPanel } from './EmailDraftPanel'
import { TriagePanel } from './TriagePanel'
import { PANE_ALONE, PANE_BESIDE, THREAD_CHILD } from './inboxLayout'
import { usePaneLayout } from './usePaneLayout'
import { channelIcon, isGroupChannel, isTriagedChannel } from './channels'
import { threadHasAi } from './inboxShape'
import { indexByProviderId, quoteOf, rowMessages, senderName, type GroupQuote } from './groupMessages'
import { indexMissingFiles, missingFor } from './missingFiles'
import { MessageScrollRoot } from './lazyObserver'
import { htmlStateOf } from './emailHtml'
import { isAllowed, setAllowed } from './externalImages'
import {
  BLOCK_EXTERNAL_IMAGES, DOWNLOAD_EXTERNAL_IMAGES,
} from './emailHtmlText'
import { useSettingsStore } from '@/stores'
import {
  MARKER_TIP,
  NOTE_MODES,
  NOTE_MODE_CONTROL_LABEL,
  NOTE_MODE_LABEL,
  isStaffNote,
  markerParts,
  noteDisplay,
  noteText,
  type NoteMode,
} from './noteMarker'
import {
  attachmentsByBody, fullTimestamp, cidRowsByBody, newMessagesLabel, placeholderOnly, separatorLabel,
} from './emailThread'
import { tourAnchor } from '@/components/tour/tourAnchor'

interface InboxConversationDetailProps {
  conversation: DetailType | null
  isLoading: boolean
  onSendMessage: (content: string, mediaRef?: string) => Promise<unknown>
  onEscalate: () => void
  onResumeAi: () => void
  onResolve: () => void
  onReopen: () => void
  onMarkRead: () => void
  onViewPreviousConversation?: (id: string) => void
  onNewConversation?: () => void
  /** Called after a successful bank-transfer staff decision (confirm or reject). */
  onBankTransferDecision?: () => void
  /** Called after a successful mark-reviewed action. */
  onMarkReviewed?: () => void
  /** SA-717: called after a triage decision or rejection went through. */
  onTriageChanged?: () => void
  /** Admin-only (inbox:archive). Omitted → button hidden. */
  onArchive?: () => void
  onUnarchive?: () => void
  /** True while the AI is composing a reply in this conversation. */
  aiTyping?: boolean
  /** SA-1381: a widget visitor is typing in this conversation. */
  visitorTyping?: boolean
  /** SA-946: open the thread at this message (`/inbox/<id>?message=<messageId>`). */
  focusMessageId?: string | null
}

/** A bubble's DOM id, for a link to a message (SA-946). */
const messageDomId = (id: string) => `message-${id}`

const senderIcons = {
  customer: User,
  ai: Bot,
  business: Briefcase,
}

function formatTimestamp(ts: number): string {
  const date = new Date(ts)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/** SA-1089: the bubble's `HH:MM`, with the full date and time on hover. */
function TimeStamp({ ts }: { ts: number }) {
  return (
    <Tip text={fullTimestamp(ts)}>
      <time dateTime={new Date(ts).toISOString()} className="text-[10px] text-muted-foreground">
        {formatTimestamp(ts)}
      </time>
    </Tip>
  )
}


/**
 * SA-1090: a decided message's verdict as one muted line where its amber box
 * was. The headline is the note's first sentence; the rest of the note
 * (the classifier's rationale included) sits behind the chevron, and the
 * rationale alone rides the line's tooltip, so a reader can check the AI's
 * reasoning without opening anything.
 */
function NoteMarker({ message }: { message: InboxMessage }) {
  const [open, setOpen] = useState(false)
  const { headline, rationale, rest } = markerParts(noteText(message))
  const line = (
    <span className="flex min-w-0 items-center gap-1.5">
      <StickyNote className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{headline}</span>
    </span>
  )
  return (
    <div className="self-center w-full max-w-[92%] px-1 text-xs text-muted-foreground" data-testid="note-marker">
      <div className="flex items-center gap-2">
        {rationale ? <Tip text={rationale}><span className="min-w-0" tabIndex={0}>{line}</span></Tip> : line}
        {rest && (
          <Tip text={open ? MARKER_TIP.hide : MARKER_TIP.show}>
            <button
              type="button"
              aria-expanded={open}
              aria-label={open ? MARKER_TIP.hide : MARKER_TIP.show}
              onClick={() => setOpen((o) => !o)}
              className="rounded p-0.5 hover:bg-muted hover:text-foreground"
            >
              <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
            </button>
          </Tip>
        )}
        <span className="ml-auto shrink-0"><TimeStamp ts={message.timestamp} /></span>
      </div>
      {open && rest && (
        <div className="mt-1 whitespace-pre-wrap pl-[18px]" data-testid="note-marker-rest">{rest}</div>
      )}
    </div>
  )
}

function MessageBubble({
  message, group, missing = [], attachments = [], threadSubject = null, noteMode = 'inline',
  cidRows = [], externalImages = false, triage,
}: {
  message: InboxMessage
  group?: { sender: string | null; quote: GroupQuote | null }
  missing?: readonly InboxMissingFile[]
  /** SA-1089: the email attachments stored as their own rows, shown under this body. */
  attachments?: readonly InboxMessage[]
  /** SA-1089: the thread header's subject, so a block repeats neither it nor its own line. */
  threadSubject?: string | null
  noteMode?: NoteMode
  /** SA-1093, SA-1110: the rows this body's frame may cite by Content-ID. */
  cidRows?: readonly InboxMessage[]
  /** SA-1093: whether the thread lets the frame load remote images. */
  externalImages?: boolean
  /** SA-1295: on a triaged email, the chip back to its row. */
  triage?: { text: string; waiting: boolean; onShow: () => void }
}) {
  // SA-1094: the set the viewer steps through for a chat message's own file.
  // Hooks sit above the returns below.
  const set = useMemo(() => viewerItems([message]), [message])
  // Staff notes (SA-243): internal handoff-context notes the backend files
  // as a Message with channel_metadata.staff_note + a `[staff note] `
  // content prefix. Metadata is the primary signal; the prefix is a
  // fallback for callers/paths that only set the content marker. These
  // must never render as a customer/business chat bubble — staff could
  // mistake internal context for something sent to the customer.
  if (isStaffNote(message)) {
    // SA-1090: an automatic verdict (an info note on a ledger row) follows
    // the viewer's preference; every note that asks for something keeps its
    // box. A hidden note is filtered out before the list is drawn.
    const display = noteDisplay(message, noteMode)
    if (display === 'hidden') return null
    if (display === 'marker') return <NoteMarker message={message} />
    return (
      <div className="self-center w-full max-w-[92%]">
        <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          <p className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-warning-strong">
            <StickyNote className="h-3 w-3" /> Internal note
          </p>
          <InboxMessageContent content={noteText(message)} />
        </div>
        <div className="mt-1 px-1">
          <TimeStamp ts={message.timestamp} />
        </div>
      </div>
    )
  }

  // SA-1127: an email reads as mail, a full-width block, never a bubble.
  if (message.channel === 'email') {
    return (
      <EmailMessage
        message={message}
        missing={missing}
        attachments={attachments}
        threadSubject={threadSubject}
        cidRows={cidRows}
        externalImages={externalImages}
        triage={triage}
      />
    )
  }

  const bodyText = message.content ?? ''
  const showText = Boolean(bodyText) && !placeholderOnly(message)
  const ownIndex = set.findIndex((it) => it.messageId === message.id)
  const ownViewer = ownIndex >= 0 ? { items: set, index: ownIndex } : undefined

  const isCustomer = message.senderType === 'customer' || message.role === 'user'
  const isAi = message.senderType === 'ai'
  const cut = readCut(message.channelMetadata)
  const isBusiness = message.senderType === 'business'
  const SenderIcon = isCustomer
    ? senderIcons.customer
    : isBusiness
      ? senderIcons.business
      : senderIcons.ai

  return (
    <div
      className={cn(
        'flex gap-2 max-w-[80%]',
        isCustomer ? 'self-start' : 'self-end flex-row-reverse'
      )}
    >
      {/* SA-1123: the AI's hand is --role-ai (SA-459), never the brand color. */}
      <div
        className={cn(
          'shrink-0 w-7 h-7 rounded-full flex items-center justify-center',
          isCustomer
            ? 'bg-muted'
            : isBusiness
              ? 'bg-info/10'
              : 'bg-role-ai/12'
        )}
      >
        <SenderIcon
          className={cn(
            'w-4 h-4',
            isCustomer
              ? 'text-muted-foreground'
              : isBusiness
                ? 'text-info'
                : 'text-role-ai'
          )}
        />
      </div>

      <div>
        {group?.sender && (
          <p className="mb-0.5 px-1 text-[11px] font-medium text-muted-foreground">{group.sender}</p>
        )}
        <div
          className={cn(
            'px-3 py-2 rounded-lg text-sm',
            isCustomer
              ? 'bg-muted text-foreground'
              : isBusiness
                ? 'bg-info/10 text-foreground'
                : 'bg-role-ai/12 text-foreground'
          )}
        >
          {group?.quote && (
            <blockquote aria-label="Quoted message" className="mb-1.5 border-l-2 border-border pl-2 text-xs text-muted-foreground">
              {group.quote.sender && <span className="font-medium">{group.quote.sender}: </span>}
              {group.quote.text}
            </blockquote>
          )}
          {message.mediaType === 'voice_call_transcript' && (
            <p className="text-xs text-muted-foreground italic mb-1 flex items-center gap-1">
              <Phone className="w-3 h-3" /> Voice call
            </p>
          )}
          {message.voiceTranscription && (
            <p className="text-xs text-muted-foreground italic mb-1">
              [Voice transcription]
            </p>
          )}
          {(message.mediaType === 'image' ||
            message.mediaType === 'document' ||
            message.mediaType === 'audio' ||
            message.mediaType === 'video') &&
            message.mediaUrl && (
            <MediaAttachment
              messageId={message.id}
              mediaType={message.mediaType}
              mediaUrl={message.mediaUrl}
              filename={attachmentMeta(message).filename}
              byteSize={attachmentMeta(message).byteSize}
              mimeType={attachmentMeta(message).mimeType}
              viewer={ownViewer}
            />
          )}
          {message.mediaType === 'audio' && !message.mediaUrl && (
            <p className="text-xs text-muted-foreground italic mb-1">
              Voice note unavailable
            </p>
          )}
          <MissingFileLines missing={missing} />
          {showText && <InboxMessageContent content={bodyText} />}
        </div>
        {isAi && cut && (
          <p className="mt-1 px-1 text-xs text-muted-foreground">{cutLine(cut.reason)}</p>
        )}
        <div className="flex items-center gap-2 mt-1 px-1">
          <TimeStamp ts={message.timestamp} />
          {message.channelStatus &&
            (message.channelStatus === 'played' ? (
              <span className="text-[10px] text-primary flex items-center gap-0.5">
                <Play className="w-2.5 h-2.5" />
                played
              </span>
            ) : (
              <span className="text-[10px] text-muted-foreground">
                {message.channelStatus}
              </span>
            ))}
          {isAi && (
            <span className="text-[10px] text-role-ai">AI</span>
          )}
          {isBusiness && (
            <span className="text-[10px] text-info">Manual</span>
          )}
        </div>
      </div>
    </div>
  )
}

function TypingBubble({ personaName }: { personaName: string | null }) {
  const SenderIcon = senderIcons.ai
  return (
    <div className="flex gap-2 max-w-[80%] self-end flex-row-reverse">
      <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center bg-role-ai/12">
        <SenderIcon className="w-4 h-4 text-role-ai" />
      </div>
      <div>
        <div className="px-3 py-2 rounded-lg text-sm bg-role-ai/12 text-foreground">
          <span aria-hidden="true" className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-role-ai/60 animate-bounce" />
            <span className="w-1.5 h-1.5 rounded-full bg-role-ai/60 animate-bounce [animation-delay:150ms]" />
            <span className="w-1.5 h-1.5 rounded-full bg-role-ai/60 animate-bounce [animation-delay:300ms]" />
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5 text-right">
          {`${personaName || 'AI'} is typing…`}
        </p>
      </div>
    </div>
  )
}

const resolvedReasonLabels: Record<string, string> = {
  manual: 'Resolved',
  timeout: 'Auto-resolved (timeout)',
  stale_cleanup: 'Auto-resolved (inactive)',
  superseded: 'Auto-resolved (superseded)',
  triaged: 'Auto-resolved (triaged)',
}

export function InboxConversationDetail({
  conversation,
  isLoading,
  onSendMessage,
  onEscalate,
  onResumeAi,
  onResolve,
  onReopen,
  onMarkRead,
  onViewPreviousConversation,
  onNewConversation,
  onBankTransferDecision,
  onMarkReviewed,
  onTriageChanged,
  onArchive,
  onUnarchive,
  aiTyping,
  visitorTyping,
  focusMessageId,
}: InboxConversationDetailProps) {
  const [input, setInput] = useState('')
  const pingTeamTyping = useTeamTypingPing(conversation?.id, conversation?.channel)
  const [isSending, setIsSending] = useState(false)
  const [isDeciding, setIsDeciding] = useState(false)
  const [decisionError, setDecisionError] = useState<string | null>(null)
  const [decisionNotice, setDecisionNotice] = useState<string | null>(null)
  const [composerError, setComposerError] = useState<string | null>(null)
  const [isReviewing, setIsReviewing] = useState(false)
  const [stagedFile, setStagedFile] = useState<File | null>(null)
  const [showLibrary, setShowLibrary] = useState(false)
  const [showSaveScenario, setShowSaveScenario] = useState(false)
  const canSaveScenario = useCanAccessFeature('replay_lab', 'workflows:configure')
  // SA-1090: how the pane shows an automatic verdict, the viewer's own
  // preference (synced with `ui`).
  const noteMode = useSettingsStore((st) => st.ui.inboxNotes)
  const setInboxNotes = useSettingsStore((st) => st.setInboxNotes)
  // SA-1093 (spec R4): per viewer and thread. SA-1110: derived during render,
  // so a remembered choice reaches the frame on its first render and a thread
  // never borrows the previous one's. SA-1122: a stored choice is read again
  // (the counter moves), so returning to a thread reads storage afresh; the
  // override holds a choice only when storage refused it, for its own thread.
  const conversationId = conversation?.id ?? null
  const [imagesRead, setImagesRead] = useState(0)
  const remembered = useMemo(
    () => (conversationId ? isAllowed(conversationId) : false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversationId, imagesRead],
  )
  const [imagesOverride, setImagesOverride] = useState<{ for: string; allowed: boolean } | null>(null)
  const externalImages = imagesOverride && imagesOverride.for === conversationId ? imagesOverride.allowed : remembered
  const hasHtmlBody = Boolean(conversation?.messages.some((m) => m.channel === 'email' && htmlStateOf(m) === 'html'))
  const toggleExternalImages = () => {
    if (!conversationId) return
    const next = !externalImages
    if (setAllowed(conversationId, next)) {
      setImagesOverride(null)
      setImagesRead((n) => n + 1)
    } else {
      setImagesOverride({ for: conversationId, allowed: next })
    }
  }
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  // SA-1110 (R1): the pane's scroll box, handed to the inline players' one observer.
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null)
  const detachScrollRef = useRef<(() => void) | null>(null)
  // Whether the pane is pinned to the bottom. Updated on user scroll; drives
  // whether late-loading content (attachment images) re-pins to the bottom.
  const atBottomRef = useRef(true)
  const markedReadRef = useRef<string | null>(null)
  const lastMessageIdRef = useRef<string | null>(null)
  // SA-946: set once a focus scroll has run; a new last message then leaves the
  // viewer where the link put them until they scroll back near the bottom.
  const heldFocusRef = useRef(false)
  // SA-1308: a jump's landing, held while the thread loads until the person's
  // first input; while it holds it wins over the bottom pin.
  const holdRef = useRef<LandingHold | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024 // WhatsApp image cap
  const MAX_DOC_BYTES = 100 * 1024 * 1024 // WhatsApp document cap

  const maxBytesForFile = (file: File) =>
    file.type.startsWith('image/') ? MAX_IMAGE_BYTES : MAX_DOC_BYTES
  const tooLargeMessage = (file: File) =>
    file.type.startsWith('image/')
      ? 'Image is too large (max 5 MB for images).'
      : 'File is too large (max 100 MB).'

  // SA-1089: messages that arrived while the reader was scrolled up. The
  // pane no longer jumps under them; a pill offers the jump instead.
  const [pendingNew, setPendingNew] = useState(0)
  const conversationIdRef = useRef<string | null>(null)

  // Jump to the bottom when the conversation changes, or when a new message
  // arrives while the reader is at the bottom. Instant (not smooth) and
  // deferred a frame: a smooth animation targets the height at call time,
  // but attachment images finish loading afterward and grow the content, so
  // a smooth scroll reliably lands short. A reader who has scrolled up is
  // left where they are and the pill counts what arrived (SA-1089).
  useEffect(() => {
    const messages = conversation?.messages
    if (!messages?.length) return
    const switched = conversationIdRef.current !== (conversation?.id ?? null)
    conversationIdRef.current = conversation?.id ?? null
    const lastId = messages[messages.length - 1].id
    if (lastId !== lastMessageIdRef.current) {
      const previous = lastMessageIdRef.current
      lastMessageIdRef.current = lastId
      if (switched) setPendingNew(0)
      if (!switched && !atBottomRef.current) {
        const at = previous ? messages.findIndex((m) => m.id === previous) : -1
        setPendingNew((n) => n + (at >= 0 ? messages.length - at - 1 : 1))
        return
      }
      heldFocusRef.current = false
      atBottomRef.current = true
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }, [conversation?.messages, conversation?.id])

  const jumpToLatest = useCallback(() => {
    holdRef.current?.release()
    const el = scrollContainerRef.current
    if (el) el.scrollTop = el.scrollHeight
    atBottomRef.current = true
    heldFocusRef.current = false
    setPendingNew(0)
  }, [])

  // SA-946: a link to a message, or "Show in thread", scrolls to it and rings
  // it once instead of pinning to the bottom. Declared after the bottom pin
  // so its frame runs last; `seq` lets the same message be shown twice.
  // SA-1308: it lands through `focusLanding` (a tall message at its top, not
  // its midpoint), and the ring keeps its request, so a repeat restarts it.
  const [focus, setFocus] = useState<{ id: string; seq: number } | null>(null)
  const [ringed, setRinged] = useState<{ id: string; seq: number } | null>(null)
  const focusedRef = useRef<string | null>(null)
  // SA-1295: an email's chip asks the Triage panel to show its row.
  const [rowFocus, setRowFocus] = useState<{ ledgerId: string; seq: number } | null>(null)
  // SA-1300: once the panel has shown the row, the request is spent.
  const dropRowFocus = useCallback(
    (request: { ledgerId: string; seq: number }) => setRowFocus((curr) => (curr === request ? null : curr)),
    [],
  )
  useEffect(() => {
    holdRef.current?.release()
    heldFocusRef.current = false
    setFocus(focusMessageId ? { id: focusMessageId, seq: Date.now() } : null)
    setRowFocus(null)
  }, [focusMessageId, conversation?.id])
  const showMessage = useCallback((id: string) => setFocus({ id, seq: Date.now() }), [])
  useEffect(() => {
    if (!focus || !conversation?.messages.some((m) => m.id === focus.id)) return
    const key = `${focus.id}:${focus.seq}`
    if (focusedRef.current === key) return
    const frame = requestAnimationFrame(() => {
      const target = () => document.getElementById(messageDomId(focus.id))
      const scroller = scrollContainerRef.current
      if (!target() || !scroller) return
      focusedRef.current = key
      atBottomRef.current = false
      heldFocusRef.current = true
      holdRef.current?.release()
      holdRef.current = holdLanding(scroller, target)
      setRinged({ id: focus.id, seq: focus.seq })
    })
    return () => cancelAnimationFrame(frame)
  }, [focus, conversation?.messages])
  useEffect(() => {
    if (!ringed) return
    const timer = window.setTimeout(() => setRinged(null), 2500)
    return () => window.clearTimeout(timer)
  }, [ringed])

  // Callback ref for the message pane. The pane is replaced by a full-height
  // spinner on any non-silent refetch, so the container unmounts and the
  // replacement node starts at scrollTop 0 — while the jump-to-bottom effect
  // above sees an unchanged last message id and stays silent. Owning the pin,
  // the scroll listener and the ResizeObserver here (rather than in an effect
  // keyed on conversation.id) makes the thread bottom-stable across ANY
  // remount, not just the one that prompted this.
  const attachScrollContainer = useCallback((el: HTMLDivElement | null) => {
    detachScrollRef.current?.()
    detachScrollRef.current = null
    scrollContainerRef.current = el
    setScrollRoot(el)
    if (!el) return

    el.scrollTop = el.scrollHeight
    atBottomRef.current = true

    const onScroll = () => {
      // SA-1308: while a landing holds, its own scroll never turns the pin back on.
      if (holdRef.current?.active) return
      atBottomRef.current = isNearBottom(el.scrollHeight, el.scrollTop, el.clientHeight)
      if (atBottomRef.current) {
        heldFocusRef.current = false
        setPendingNew(0)
      }
    }
    el.addEventListener('scroll', onScroll, { passive: true })

    // Attachment images load async over an auth-gated route, so they render
    // after this pin and grow the thread. Re-pin as it grows, unless the user
    // has scrolled up to read history. A held landing lands again instead.
    const content = el.firstElementChild
    const observer = new ResizeObserver(() => {
      const hold = holdRef.current
      if (hold?.active) hold.reapply()
      else if (atBottomRef.current) el.scrollTop = el.scrollHeight
    })
    if (content) observer.observe(content)

    detachScrollRef.current = () => {
      holdRef.current?.release()
      el.removeEventListener('scroll', onScroll)
      observer.disconnect()
    }
  }, [])

  // Mark as read when conversation is opened
  useEffect(() => {
    if (conversation && markedReadRef.current !== conversation.id) {
      markedReadRef.current = conversation.id
      onMarkRead()
    }
  }, [conversation?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if ((!text && !stagedFile) || isSending) return
    setComposerError(null)
    setIsSending(true)
    try {
      let mediaRef: string | undefined
      if (stagedFile) {
        const fd = new FormData()
        fd.append('file', stagedFile)
        const up = await apiClient.fetch('/api/media/upload', { method: 'POST', body: fd })
        if (!up.ok) {
          if (up.status === 413) {
            setComposerError(
              stagedFile ? tooLargeMessage(stagedFile) : 'File is too large.',
            )
          } else {
            setComposerError("Couldn't upload attachment. Please try again.")
          }
          return
        }
        mediaRef = (await up.json()).media_ref
      }
      await onSendMessage(text, mediaRef)
      setInput('')
      setStagedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch {
      setComposerError("Couldn't upload attachment. Please try again.")
    } finally {
      setIsSending(false)
    }
  }, [input, stagedFile, isSending, onSendMessage])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Classify escalation messages by KIND so the right banner shows. A
  // delivery/custom escalation must NOT render the bank-transfer Confirm/Reject
  // controls (staging conv 1c14c441). Back-compat: a transfer_id with no
  // escalation_kind is a bank-transfer proof escalation; otherwise generic.
  const escalationKindOf = (m: InboxMessage): string => {
    const k = m.channelMetadata?.escalation_kind
    // Legacy alias (SA-50): pre-fix bank-proof escalations were filed as
    // payment_proof_review with no transfer_id — same decision surface.
    if (k === 'payment_proof_review') return 'bank_transfer'
    if (typeof k === 'string') return k
    return m.channelMetadata?.transfer_id ? 'bank_transfer' : 'generic'
  }
  const escalationMessages =
    conversation?.messages.filter(
      (m) => m.channelMetadata?.staff_action_required === true,
    ) ?? []
  const hasBankTransferDecision = escalationMessages.some(
    (m) => escalationKindOf(m) === 'bank_transfer',
  )
  const genericEscalationMsg = escalationMessages.find(
    (m) => escalationKindOf(m) !== 'bank_transfer',
  )
  const escalationReason = genericEscalationMsg
    ? genericEscalationMsg.content.replace(/^\[escalation\]\s*/, '')
    : ''

  // Detect whether any message carries the lighter "needs review" flag (no escalation yet).
  const needsReview = conversation?.messages.some(
    (m) => m.channelMetadata?.bank_transfer_review_required === true,
  ) ?? false

  // SA-946: a WhatsApp group thread is a stream of several senders (SA-945
  // S1): bubbles say who spoke and what they quoted, nothing is sent from
  // here (parent spec W23), and the AI never replies.
  const isGroup = isGroupChannel(conversation?.channel)
  // SA-1092 §2.3: Take Over, Resume AI, Save scenario and the overrides-AI
  // wording belong to a thread that has an AI — a direct chat, never a router thread.
  const hasAi = conversation ? threadHasAi(conversation) : false
  const byProviderId = useMemo(
    () => (isGroup && conversation ? indexByProviderId(conversation.messages) : null),
    [isGroup, conversation],
  )
  // SA-1009: the files each message could not load, by its provider id.
  const missingIndex = useMemo(() => indexMissingFiles(conversation?.missingFiles), [conversation])

  // SA-717 (spec §5): on a triaged thread (email, and since SA-946 a
  // WhatsApp group) with triage to show, the Triage panel takes the generic
  // escalation banner's place.
  const triage = isTriagedChannel(conversation?.channel) && conversation?.triage && conversation.triage.rows.length > 0
    ? conversation.triage
    : null
  // SA-1347: the pane's own width picks the layout (the hook is above the early returns).
  const [paneRef, layout] = usePaneLayout()
  const beside = layout === 'beside'
  const hasPanel = !!(conversation && triage)
  const zone = useDefaultTimezone()
  const groupRows = useMemo(
    () => (isGroup && triage && byProviderId ? rowMessages(triage.rows, byProviderId, zone) : null),
    [isGroup, triage, byProviderId, zone],
  )
  // SA-1295: on an email thread, each row's email, and the chip each triaged email carries.
  const emailRows = useMemo(
    () => (conversation?.channel === 'email' && triage
      ? emailRowMessages(triage.rows, conversation.messages, conversation.email?.subject ?? null, zone)
      : null),
    [conversation, triage, zone],
  )
  const chips = useMemo(() => (emailRows && triage ? emailChips(triage.rows, emailRows) : null), [emailRows, triage])

  const handleMarkReviewed = async () => {
    if (!conversation || isReviewing) return
    setIsReviewing(true)
    try {
      const response = await apiClient.fetch(
        `/api/inbox/conversations/${conversation.id}/mark-reviewed`,
        { method: 'PUT' },
      )
      if (!response.ok) throw new Error('mark-reviewed failed')
      onMarkReviewed?.()
    } catch {
      /* non-fatal; SSE will reconcile */
    } finally {
      setIsReviewing(false)
    }
  }

  const handleBankTransferDecision = async (decision: 'confirmed' | 'rejected') => {
    if (!conversation || isDeciding) return
    setIsDeciding(true)
    setDecisionError(null)
    setDecisionNotice(null)
    try {
      const response = await apiClient.fetch(
        `/api/inbox/conversations/${conversation.id}/bank-transfer-decision`,
        {
          method: 'PUT',
          body: JSON.stringify({ decision }),
        },
      )
      if (!response.ok) throw new Error('Decision call failed')
      // SA-57: `resumed: false` means a concurrent request (another staff
      // member, or a retry) already took the decision — the row lock made
      // this call a no-op. Say so instead of presenting a silent success;
      // an unreadable body degrades to the legacy silent-success shape.
      const data = await response.json().catch(() => null)
      if (data?.resumed === false) {
        setDecisionNotice('Already decided by someone else — refreshing…')
      }
      // SSE conversation_updated will trigger a silent detail refresh;
      // also call the optional parent callback so InboxPage can re-fetch.
      onBankTransferDecision?.()
    } catch {
      setDecisionError('Could not submit decision. Please try again.')
    } finally {
      setIsDeciding(false)
    }
  }

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Select a conversation to view</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  // SA-99: manual-override composer is disabled for email conversations
  // whose mailbox isn't authorized to send outbound — the AI draft panel
  // above is the only send path until an admin re-authorizes it.
  const emailComposerDisabled =
    conversation.channel === 'email' && conversation.emailSendAuthorized !== true

  // SA-946: one icon map for the list and the header (channels.ts).
  const HeaderIcon = channelIcon(conversation.channel)
  const headerTone = conversation.channel === 'whatsapp' || isGroup ? 'text-green-500' : 'text-blue-500'

  // Group messages by date
  let lastDate = ''
  // SA-1089: an email's attachments render under its body bubble.
  const thread = attachmentsByBody(conversation.messages)
  // SA-1093, SA-1110: each body's cid rows, for its frame.
  const cidRows = cidRowsByBody(conversation.messages)
  // SA-1090: a hidden verdict leaves the list before it is drawn, so it
  // contributes no date separator.
  const shown = thread.messages.filter((m) => noteDisplay(m, noteMode) !== 'hidden')
  const threadSubject = conversation.email?.subject ?? null
  const chipOf = (messageId: string) => {
    const chip = chips?.get(messageId)
    return chip
      ? { text: chip.text, waiting: chip.waiting, onShow: () => setRowFocus({ ledgerId: chip.ledgerId, seq: Date.now() }) }
      : undefined
  }

  return (
    <>
    <div
      ref={paneRef}
      data-testid="message-pane"
      className={beside ? cn('flex-1 min-w-0', hasPanel ? PANE_BESIDE : PANE_ALONE) : 'flex-1 flex flex-col min-w-0'}
    >
      {/* Header. flex-wrap + the min-w-0/truncate chain keep the row inside
          the detail column: the action buttons are whitespace-nowrap+shrink-0
          (basis Button), so without wrapping they overflow under the order
          sidebar at moderate window widths (2026-07-18 staging bug). */}
      <div className={cn('flex flex-wrap items-center justify-between gap-y-2 px-4 py-3 border-b border-border', beside && THREAD_CHILD.header)}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <HeaderIcon className={cn('w-4 h-4', headerTone)} />
            <span className="font-medium text-foreground truncate">
              {conversation.customerDisplayName || conversation.customerPhone || 'Unknown'}
            </span>
          </div>
          {conversation.personaName && (
            <Badge variant="secondary" className="text-xs">
              {conversation.personaName}
            </Badge>
          )}
          <Badge
            variant={conversation.status === 'human' ? 'destructive' : conversation.status === 'resolved' ? 'secondary' : 'secondary'}
            className={cn('text-xs', conversation.status === 'resolved' && 'text-muted-foreground')}
          >
            {conversation.status === 'resolved' && conversation.resolvedReason
              ? resolvedReasonLabels[conversation.resolvedReason] || 'Resolved'
              : conversation.status}
          </Badge>
          {/* SA-1079: a router mailbox's thread (the server serves `triage`
              only there) has no AI to pause. */}
          {!conversation.aiAutoReplyEnabled && conversation.status !== 'resolved' && !conversation.triage && (
            <Badge variant="secondary" className="text-xs text-warning-strong border-warning">
              AI paused
            </Badge>
          )}
          {conversation.previousConversationId && onViewPreviousConversation && (
            <button
              onClick={() => onViewPreviousConversation(conversation.previousConversationId!)}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
            >
              <History className="w-3 h-3" />
              Previous conversation
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canSaveScenario && hasAi && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowSaveScenario(true)}
                className="text-xs"
              >
                <FlaskConical className="w-3 h-3 mr-1" />
                Save scenario
              </Button>
              <div className="w-px h-5 bg-border" />
            </>
          )}
          {onNewConversation && conversation.channel === 'whatsapp' && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={onNewConversation}
                className="text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                New Conversation
              </Button>
              <div className="w-px h-5 bg-border" />
            </>
          )}
          {onArchive && !conversation.archivedAt && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onArchive}
              className="text-xs"
            >
              <Archive className="w-3 h-3 mr-1" />
              Archive
            </Button>
          )}
          {onUnarchive && conversation.archivedAt && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onUnarchive}
              className="text-xs"
            >
              <ArchiveRestore className="w-3 h-3 mr-1" />
              Unarchive
            </Button>
          )}
          {isTriagedChannel(conversation.channel) && (
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              {NOTE_MODE_CONTROL_LABEL}
              <select
                aria-label={NOTE_MODE_CONTROL_LABEL}
                value={noteMode}
                onChange={(e) => setInboxNotes(e.target.value as NoteMode)}
                className="text-xs rounded-md border border-border bg-background px-2 py-1 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
              >
                {NOTE_MODES.map((m) => (
                  <option key={m} value={m}>{NOTE_MODE_LABEL[m]}</option>
                ))}
              </select>
            </label>
          )}
          {conversation.channel === 'email' && hasHtmlBody && (
            <Button variant="secondary" size="sm" className="text-xs" onClick={toggleExternalImages}>
              {externalImages ? BLOCK_EXTERNAL_IMAGES : DOWNLOAD_EXTERNAL_IMAGES}
            </Button>
          )}
          {hasAi && conversation.status !== 'human' && conversation.status !== 'resolved' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onEscalate}
              className="text-xs"
            >
              <AlertTriangle className="w-3 h-3 mr-1" />
              Take Over
            </Button>
          )}
          {/* Resume AI stays on EVERY human thread: it is the one way out of
              `human` (SA-946: a group thread can be in human with no Take Over). */}
          {conversation.status === 'human' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onResumeAi}
              className="text-xs"
            >
              <Play className="w-3 h-3 mr-1" />
              Resume AI
            </Button>
          )}
          {conversation.status !== 'resolved' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onResolve}
              className="text-xs text-success hover:text-success/80"
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              Resolve
            </Button>
          )}
          {conversation.status === 'resolved' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onReopen}
              className="text-xs"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Reopen
            </Button>
          )}
        </div>
      </div>

      {conversation.channel === 'email' && conversation.email && (
        <InboxEmailHeader email={conversation.email} className={beside ? THREAD_CHILD.emailHeader : undefined} />
      )}

      {/* Messages */}
      <div className={cn('relative flex-1 min-h-0 flex flex-col', beside && THREAD_CHILD.messages)}>
      <div
        ref={attachScrollContainer}
        data-testid="message-scroll"
        {...tourAnchor('inbox.thread.messages')}
        className="flex-1 overflow-y-auto px-4 py-4"
      >
        <MessageScrollRoot.Provider value={scrollRoot}>
        <div className="flex flex-col gap-3">
          {shown.map((msg) => {
            const msgDate = separatorLabel(msg.timestamp)
            const showDate = msgDate !== lastDate
            lastDate = msgDate

            return (
              <div
                key={msg.id}
                id={messageDomId(msg.id)}
                data-focused={ringed?.id === msg.id ? 'true' : undefined}
                // SA-1127: a flex column, so a bubble's self-start / self-end and a
                // note's self-center reach the pane's edges.
                className={cn('flex flex-col rounded-lg transition-shadow', ringed?.id === msg.id && 'ring-2 ring-primary')}
              >
                {showDate && (
                  <div className="flex items-center justify-center my-3">
                    <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                      {msgDate}
                    </span>
                  </div>
                )}
                <MessageBubble
                  message={msg}
                  group={byProviderId ? { sender: senderName(msg, byProviderId), quote: quoteOf(msg, byProviderId) } : undefined}
                  missing={missingFor(msg, missingIndex)}
                  attachments={thread.attachments.get(msg.id)}
                  threadSubject={threadSubject}
                  noteMode={noteMode}
                  cidRows={cidRows.get(msg.id)}
                  externalImages={externalImages}
                  triage={chipOf(msg.id)}
                />
              </div>
            )
          })}
          {aiTyping && <TypingBubble personaName={conversation.personaName} />}
          {visitorTyping && (
            <p className="text-xs text-muted-foreground">
              {conversation.customerDisplayName || 'Visitor'} is typing…
            </p>
          )}
        </div>
        </MessageScrollRoot.Provider>
      </div>
      {pendingNew > 0 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
          <button
            type="button"
            onClick={jumpToLatest}
            className="pointer-events-auto rounded-full border border-border bg-card px-3 py-1 text-xs font-medium shadow-sm hover:bg-muted"
          >
            {newMessagesLabel(pendingNew)} ↓
          </button>
        </div>
      )}
      </div>

      {conversation && triage && (
        <TriagePanel
          // Remount per conversation: an error or an open kind select must
          // not follow the person to another thread. The key is prefixed: it
          // shares a parent with EmailDraftPanel, and two siblings keyed by the
          // same id duplicate on any re-render (SA-1110).
          key={`triage-${conversation.id}`}
          conversationId={conversation.id}
          triage={triage}
          linkedOrderId={conversation.linkedOrderId}
          linkedOrderNumber={conversation.linkedOrderNumber}
          records={conversation.records}
          onChanged={() => onTriageChanged?.()}
          group={groupRows ? { messages: groupRows, onShowMessage: showMessage } : undefined}
          email={emailRows ? { messages: emailRows, onShowMessage: showMessage } : undefined}
          focusRow={rowFocus}
          onFocusRowShown={dropRowFocus}
          layout={layout}
        />
      )}

      {/* Bank-transfer staff override — shown when a proof-escalation message exists */}
      {hasBankTransferDecision && (
        <div className={cn('border-t border-border px-4 py-3 bg-warning/10', beside && THREAD_CHILD.bankTransfer)}>
          <p className="text-xs text-warning-strong font-medium mb-2">
            Bank transfer proof requires your review
          </p>
          {decisionError && (
            <p className="text-xs text-destructive mb-2" role="alert">
              {decisionError}
            </p>
          )}
          {decisionNotice && (
            <p className="text-xs text-muted-foreground mb-2" role="status">
              {decisionNotice}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => handleBankTransferDecision('confirmed')}
              disabled={isDeciding}
              className="text-xs bg-success hover:bg-success/90 text-success-foreground border-success"
            >
              {isDeciding ? (
                <Spinner className="w-3 h-3 mr-1" />
              ) : (
                <CheckCircle className="w-3 h-3 mr-1" />
              )}
              Confirm
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleBankTransferDecision('rejected')}
              disabled={isDeciding}
              className="text-xs text-destructive hover:text-destructive/80 border-destructive"
            >
              {isDeciding ? (
                <Spinner className="w-3 h-3 mr-1" />
              ) : (
                <XCircle className="w-3 h-3 mr-1" />
              )}
              Reject
            </Button>
          </div>
        </div>
      )}

      {/* Generic escalation banner — delivery / custom / other staff handoffs
          that are NOT bank-transfer proof reviews. */}
      {genericEscalationMsg && !triage && (
        <div
          className={cn('border-t border-border px-4 py-3 bg-warning/10', beside && THREAD_CHILD.escalation)}
          role="region"
          aria-label="escalation"
        >
          <p className="text-xs text-warning-strong font-medium mb-1">
            Escalated for human handling
          </p>
          {escalationReason && (
            <p className="text-xs text-foreground mb-2">
              {escalationReason}
            </p>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={handleMarkReviewed}
            disabled={isReviewing}
            className="text-xs"
          >
            {isReviewing ? (
              <Spinner className="w-3 h-3 mr-1" />
            ) : (
              <CheckCircle className="w-3 h-3 mr-1" />
            )}
            Mark handled
          </Button>
        </div>
      )}

      {/* "Needs review" panel — shown when proof submitted but no staff
          escalation (of any kind) yet. */}
      {needsReview && !hasBankTransferDecision && !genericEscalationMsg && (
        <div className={cn('border-t border-border px-4 py-3 bg-warning/10', beside && THREAD_CHILD.needsReview)}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-warning-strong font-medium">
              Bank transfer proof — needs review
            </p>
            <Button size="sm" variant="secondary" onClick={handleMarkReviewed} disabled={isReviewing} className="text-xs">
              {isReviewing ? <Spinner className="w-3 h-3 mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
              Mark reviewed
            </Button>
          </div>
        </div>
      )}

      {/* AI email draft review (SA-99) — sits above the composer for email
          conversations; the composer itself becomes a manual-override
          fallback for that channel. Gated the same as the composer below:
          a resolved conversation gets no send surface at all. */}
      {conversation.channel === 'email' && conversation.status !== 'resolved' && (
        <EmailDraftPanel
          // Remount per conversation: EmailDraftPanel owns 4 useMutation
          // objects (error/pending state) that otherwise persist across a
          // conversation switch and can show conversation A's stale error
          // or in-flight spinner while viewing conversation B.
          key={`email-draft-${conversation.id}`}
          conversationId={conversation.id}
          sendAuthorized={conversation.emailSendAuthorized === true}
          onSent={() => onMarkRead()}
          className={beside ? THREAD_CHILD.draft : undefined}
        />
      )}

      {/* Input — hidden for resolved conversations */}
      {isGroup && (
        <p data-testid="group-no-composer" className={cn('border-t border-border px-4 py-3 text-xs text-muted-foreground', beside && THREAD_CHILD.composer)}>
          Replies to this group are sent from the phone.
        </p>
      )}
      {!isGroup && conversation.status !== 'resolved' && (
        <div className={cn('border-t border-border p-4', beside && THREAD_CHILD.composer)}>
          <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              data-testid="composer-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null
                if (file && file.size > maxBytesForFile(file)) {
                  setComposerError(tooLargeMessage(file))
                  if (fileInputRef.current) fileInputRef.current.value = ''
                  return
                }
                setComposerError(null)
                setStagedFile(file)
              }}
            />
            <button
              type="button"
              aria-label="Attach file"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-muted-foreground hover:text-foreground shrink-0"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <button
              type="button"
              aria-label="Send from library"
              onClick={() => setShowLibrary(true)}
              className="p-2 text-muted-foreground hover:text-foreground shrink-0"
            >
              <ImageIcon className="w-4 h-4" />
            </button>
            <div className="flex-1 flex flex-col gap-1">
              {composerError && (
                <p
                  data-testid="composer-error"
                  className="text-xs text-destructive"
                  role="alert"
                >
                  {composerError}
                </p>
              )}
              {stagedFile && (
                <span
                  data-testid="composer-staged-chip"
                  className="text-xs text-muted-foreground truncate max-w-[200px] flex items-center gap-1"
                >
                  {stagedFile.name}
                  <button
                    type="button"
                    aria-label="Remove attachment"
                    className="ml-1 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setStagedFile(null)
                      setComposerError(null)
                      if (fileInputRef.current) fileInputRef.current.value = ''
                    }}
                  >
                    ×
                  </button>
                </span>
              )}
              <textarea
                value={input}
                onChange={(e) => {
                  setInput(e.target.value)
                  if (e.target.value.trim()) pingTeamTyping()
                }}
                onKeyDown={handleKeyDown}
                disabled={emailComposerDisabled}
                placeholder={
                  !hasAi
                    ? 'Reply by email...'
                    : conversation.status === 'human'
                      ? 'Send a message as the business...'
                      : 'Send a manual message (overrides AI)...'
                }
                rows={1}
                className={cn(
                  'w-full resize-none rounded-lg border border-border',
                  'bg-card px-3 py-2 text-sm',
                  'focus:outline-hidden focus:ring-2 focus:ring-ring',
                  'placeholder:text-muted-foreground',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              />
              {emailComposerDisabled && (
                <p className="text-xs text-muted-foreground">
                  Outbound email is not enabled for this mailbox — enable outbound and
                  re-consent in Admin → Mailboxes.
                </p>
              )}
            </div>
            <Button
              size="sm"
              data-testid="composer-send"
              onClick={handleSend}
              disabled={(!input.trim() && !stagedFile) || isSending || emailComposerDisabled}
            >
              {isSending ? (
                <Spinner size="sm" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
    <AssetLibraryPicker
      open={showLibrary}
      onClose={() => setShowLibrary(false)}
      onPick={async (mediaRef) => {
        setIsSending(true)
        try { await onSendMessage(input.trim(), mediaRef); setInput('') }
        finally { setIsSending(false) }
      }}
    />
    {showSaveScenario && (
      <SaveScenarioModal
        conversationId={conversation.id}
        defaultName={`${conversation.customerDisplayName || conversation.customerPhone || 'Conversation'} — ${new Date().toLocaleDateString()}`}
        open
        onOpenChange={(o) => { if (!o) setShowSaveScenario(false) }}
      />
    )}
    </>
  )
}
